import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Generation, VehicleUpload } from "@/lib/db/schema";

export interface GenerationWithRim extends Generation {
  rim?: { id: string; name: string; imageUrl: string } | null;
  userRim?: { id: string; name: string; imageUrl: string; brand?: string; sku?: string } | null;
  vehicleUpload?: VehicleUpload | null;
  vehicleImageUrl: string | null;
}

const PENDING_GENERATIONS_STORAGE_KEY = "rim_ai_pending_generations_v1";

interface PendingGenerationEntry {
  id: string;
  notified?: boolean;
}

function readPendingGenerationsFromStorage(): PendingGenerationEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(PENDING_GENERATIONS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((item) => {
        if (!item || typeof item !== "object") return null;
        const id = (item as { id?: unknown }).id;
        if (typeof id !== "string") return null;
        const notified = (item as { notified?: unknown }).notified;
        return { id, notified: typeof notified === "boolean" ? notified : false } as PendingGenerationEntry;
      })
      .filter((item): item is PendingGenerationEntry => item !== null);
  } catch {
    return [];
  }
}

function writePendingGenerationsToStorage(entries: PendingGenerationEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      PENDING_GENERATIONS_STORAGE_KEY,
      JSON.stringify(entries),
    );
  } catch {
    // Swallow storage errors (e.g. quota, private mode) – not critical for core flow.
  }
}

export function addPendingGenerationToStorage(id: string) {
  if (typeof window === "undefined") return;
  const existing = readPendingGenerationsFromStorage();
  if (existing.some((entry) => entry.id === id)) return;
  const updated: PendingGenerationEntry[] = [...existing, { id, notified: false }];
  writePendingGenerationsToStorage(updated);
}

export function removePendingGenerationFromStorage(id: string) {
  if (typeof window === "undefined") return;
  const existing = readPendingGenerationsFromStorage();
  const updated = existing.filter((entry) => entry.id !== id);
  writePendingGenerationsToStorage(updated);
}

export function markPendingGenerationNotifiedInStorage(id: string) {
  if (typeof window === "undefined") return;
  const existing = readPendingGenerationsFromStorage();
  const updated = existing.map((entry) =>
    entry.id === id ? { ...entry, notified: true } : entry,
  );
  writePendingGenerationsToStorage(updated);
}

export function getPendingGenerationsFromStorage(): PendingGenerationEntry[] {
  return readPendingGenerationsFromStorage();
}

export async function fetchGenerationById(id: string): Promise<GenerationWithRim> {
  const response = await fetch(`/api/generations/${id}`);
  if (!response.ok) {
    throw new Error("Failed to fetch generation");
  }
  return response.json();
}

export function useGeneration(id: string | null) {
  const queryClient = useQueryClient();
  const lastInvalidatedGenerationIdRef = useRef<string | null>(null);

  const query = useQuery<GenerationWithRim, Error>({
    queryKey: ["generation", id],
    queryFn: async () => {
      if (!id) throw new Error("No generation ID");
      return fetchGenerationById(id);
    },
    enabled: !!id,
    staleTime: 0,
    gcTime: 5 * 60_000,
    retry: 1,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    // Poll every 2 seconds while pending/processing, stop when completed/failed
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === "completed" || status === "failed") {
        return false;
      }
      return 2000;
    },
    refetchIntervalInBackground: false,
  });

  // When the active generation finishes, refresh the generations list once.
  // This avoids polling the list endpoint and keeps history up-to-date.
  useEffect(() => {
    const data = query.data;
    if (!data?.id) return;

    if (data.status === "completed" && data.resultImageUrl) {
      // Clear any persisted pending state for this generation.
      removePendingGenerationFromStorage(data.id);

      if (lastInvalidatedGenerationIdRef.current !== data.id) {
        lastInvalidatedGenerationIdRef.current = data.id;
        queryClient.invalidateQueries({ queryKey: ["generations"] });
      }
    } else if (data.status === "failed") {
      // Failed generations should also be cleared from pending state.
      removePendingGenerationFromStorage(data.id);
    }
  }, [query.data, queryClient]);

  return query;
}

interface CreateGenerationParams {
  vehicleUploadId: string; // Reference to uploaded vehicle
  rimId?: string;
  userRimId?: string;
  resolution?: "low" | "medium" | "high"; // Optional, defaults to "low" (all generations use 1K)
  isFreePreview?: boolean;
}

interface CreateGenerationResponse {
  id: string;
  status: string;
  creditCost: number;
}

export function useCreateGeneration() {
  const queryClient = useQueryClient();

  return useMutation<CreateGenerationResponse, Error, CreateGenerationParams>({
    mutationFn: async (params) => {
      const response = await fetch("/api/generations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create generation");
      }
      
      return response.json();
    },
    onSuccess: (data, variables) => {
      // Ensure history list reflects the newly created generation (pending) without polling.
      queryClient.invalidateQueries({ queryKey: ["generations"] });
      if (variables.isFreePreview) {
        queryClient.invalidateQueries({ queryKey: ["credits"] });
      }
      // If anything is already subscribed to the new generation, prompt it to fetch.
      queryClient.invalidateQueries({ queryKey: ["generation", data.id] });

      // Persist this generation ID so we can surface completion even if the
      // user navigates away from the app and comes back later.
      addPendingGenerationToStorage(data.id);
    },
  });
}

export interface UnlockGenerationResponse {
  id: string;
  isFreePreview: boolean;
  isUnlocked: boolean;
  resultImageUrl: string | null;
  originalImageUrl: string | null;
  [key: string]: unknown;
}

export function useUnlockGeneration() {
  const queryClient = useQueryClient();
  
  return useMutation<UnlockGenerationResponse, Error, string>({
    mutationFn: async (generationId: string) => {
      const response = await fetch(`/api/generations/${generationId}/unlock`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to unlock generation");
      }
      
      return response.json();
    },
    onSuccess: (_, generationId) => {
      // Invalidate relevant queries to refetch updated generation data
      queryClient.invalidateQueries({ queryKey: ["generation", generationId] });
      queryClient.invalidateQueries({ queryKey: ["generations"] });
      queryClient.invalidateQueries({ queryKey: ["credits"] }); // Credits balance might change
    },
  });
}
